/**
 * <LeadAppointmentsPanel leadId={id} /> — Apex 2.0 Phase 2.
 *
 * Lists every appointment scheduled for this lead + lets the wholesaler
 * book a manual appointment with 3 AI-proposed slots (Apex picks the slots
 * via GET /api/apex/appointments/slots, which checks Google FreeBusy if
 * connected, else falls back to defaults).
 *
 * Endpoints used:
 *   GET    /api/apex/appointments?lead_id=...
 *   GET    /api/apex/appointments/slots
 *   POST   /api/apex/appointments
 *   PATCH  /api/apex/appointments/{id}      (status changes)
 *   DELETE /api/apex/appointments/{id}      (cancel + remove GCal event)
 */
import { useCallback, useEffect, useState } from "react";
import { Calendar, Plus, Loader2, X, Check, Trash2, CheckCircle2, Clock, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

function fmt(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function LeadAppointmentsPanel({ leadId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    if (!leadId) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/apex/appointments?lead_id=${leadId}`);
      setItems(Array.isArray(data) ? data : []);
    } catch { setItems([]); }
    setLoading(false);
  }, [leadId]);

  useEffect(() => { load(); }, [load]);

  const onPatch = async (id, body) => {
    try {
      await api.patch(`/apex/appointments/${id}`, body);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const onDelete = async (id) => {
    if (!window.confirm("Cancel this appointment? (Also removes the calendar event.)")) return;
    try {
      await api.delete(`/apex/appointments/${id}`);
      toast.success("Appointment cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  return (
    <section className="card p-5" data-testid="lead-appointments-panel">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="text-[10px] uppercase tracking-widest font-bold text-[var(--ink-3)]">
            Apex 2.0 · Phase 2
          </div>
          <h3 className="font-display font-bold text-lg flex items-center gap-2">
            <Calendar size={16} className="text-indigo-600" /> Appointments
          </h3>
        </div>
        <button type="button" className="btn-money text-xs"
          onClick={() => setCreating(true)}
          data-testid="lead-appt-schedule-btn">
          <Plus size={12} /> Schedule
        </button>
      </div>

      {loading ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-6">
          <Loader2 size={14} className="animate-spin inline mr-2" /> Loading…
        </div>
      ) : items.length === 0 ? (
        <div className="text-center text-sm text-[var(--ink-3)] py-6 border-2 border-dashed border-slate-200 rounded-lg" data-testid="lead-appt-empty">
          <Calendar size={26} className="mx-auto opacity-40 mb-2" />
          <p>No appointments scheduled.</p>
          <p className="text-[11px] mt-0.5">Apex auto-books appointments during qualifying calls — or schedule manually with "Schedule".</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="lead-appt-list">
          {items.map((a) => (
            <div key={a.id}
              className={`flex items-start gap-3 p-3 rounded-lg border ${
                a.status === "scheduled" ? "border-indigo-200 bg-indigo-50/40"
                : a.status === "completed" ? "border-emerald-200 bg-emerald-50/40"
                : "border-slate-200 bg-slate-50 opacity-70"
              }`}
              data-testid={`lead-appt-row-${a.id}`}
            >
              <div className="shrink-0 w-9 h-9 rounded-lg bg-indigo-100 text-indigo-700 flex items-center justify-center">
                <Calendar size={14} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-sm">{fmt(a.start_iso)}</div>
                <div className="text-[11px] text-[var(--ink-3)]">
                  {a.duration_min} min
                  {a.gcal_event_id ? " · Google Calendar ✓" : " · local-only"}
                  {a.apex_call_id ? " · booked by Apex" : ""}
                  {" · "} <span className="capitalize">{a.status}</span>
                </div>
                {a.notes && <div className="text-[11px] text-[var(--ink-2)] mt-1 line-clamp-2">{a.notes}</div>}
              </div>
              {a.status === "scheduled" && (
                <div className="flex flex-col items-end gap-1 shrink-0">
                  <button type="button"
                    className="btn-ghost text-xs"
                    title="Mark complete"
                    onClick={() => onPatch(a.id, { status: "completed" })}
                    data-testid={`lead-appt-done-${a.id}`}>
                    <Check size={12} className="text-emerald-600" />
                  </button>
                  <button type="button"
                    className="btn-ghost text-xs text-rose-600"
                    title="Cancel"
                    onClick={() => onDelete(a.id)}
                    data-testid={`lead-appt-cancel-${a.id}`}>
                    <Trash2 size={12} />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {creating && (
        <ScheduleModal
          leadId={leadId}
          onClose={() => setCreating(false)}
          onCreated={() => { setCreating(false); load(); }}
        />
      )}
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Schedule modal — pulls 3 AI-proposed slots from the backend.
// ─────────────────────────────────────────────────────────────────────
function ScheduleModal({ leadId, onClose, onCreated }) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [picked, setPicked] = useState(null);
  const [duration, setDuration] = useState(15);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get(`/apex/appointments/slots?duration_min=${duration}`)
      .then((r) => { if (alive) setSlots(r.data?.slots || []); })
      .catch(() => { if (alive) setSlots([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [duration]);

  const submit = async () => {
    if (!picked) {
      toast.error("Pick a time slot");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/apex/appointments", {
        lead_id: leadId,
        start_iso: picked.start_iso,
        duration_min: duration,
        notes: notes || undefined,
        push_to_google: true,
      });
      toast.success("Appointment scheduled");
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to schedule");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 backdrop-blur-sm p-4"
         onClick={onClose} data-testid="lead-appt-modal">
      <div className="bg-white w-full max-w-md rounded-2xl shadow-xl max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-[var(--line)]">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-indigo-700">Apex 2.0</div>
            <div className="font-display font-bold text-lg mt-0.5">Schedule appointment</div>
          </div>
          <button type="button" className="btn-ghost" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="p-5">
          <label className="field-label">Duration</label>
          <select className="input mb-4" value={duration}
            onChange={(e) => { setDuration(Number(e.target.value)); setPicked(null); }}
            data-testid="lead-appt-modal-duration"
          >
            {[10, 15, 20, 30, 45, 60].map((m) => <option key={m} value={m}>{`${m} min`}</option>)}
          </select>

          <div className="text-[11px] uppercase tracking-widest font-bold text-[var(--ink-3)] mb-2 flex items-center gap-1">
            <Clock size={11} /> AI-proposed slots
          </div>
          {loading ? (
            <div className="text-sm text-[var(--ink-3)] text-center py-3"><Loader2 size={13} className="animate-spin inline mr-1.5" /> Loading…</div>
          ) : slots.length === 0 ? (
            <div className="text-sm text-[var(--ink-3)] text-center py-3">No slots available.</div>
          ) : (
            <div className="space-y-1.5 mb-4" data-testid="lead-appt-modal-slots">
              {slots.map((s) => (
                <button key={s.start_iso}
                  type="button"
                  onClick={() => setPicked(s)}
                  className={`w-full text-left p-2.5 rounded-lg border-2 transition-all ${
                    picked?.start_iso === s.start_iso
                      ? "border-indigo-500 bg-indigo-50"
                      : "border-slate-200 bg-white hover:border-slate-300"
                  }`}
                  data-testid={`lead-appt-modal-slot-${s.start_iso}`}
                >
                  <div className="font-bold text-sm">{fmt(s.start_iso)}</div>
                  <div className="text-[11px] text-[var(--ink-3)]">{s.label}</div>
                  {picked?.start_iso === s.start_iso && (
                    <CheckCircle2 size={12} className="text-indigo-600 inline mt-0.5" />
                  )}
                </button>
              ))}
            </div>
          )}

          <label className="field-label">Notes (optional)</label>
          <textarea className="textarea mb-4" rows={2} value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Anything to put in the calendar invite description…"
            data-testid="lead-appt-modal-notes"
          />

          <div className="flex justify-end gap-2 pt-3 border-t border-[var(--line)]">
            <button type="button" className="btn-outline" onClick={onClose}>Cancel</button>
            <button type="button" className="btn-money" disabled={submitting || !picked}
              onClick={submit}
              data-testid="lead-appt-modal-submit">
              {submitting ? <Loader2 size={13} className="animate-spin" /> : <Calendar size={13} />}
              {submitting ? "Booking…" : "Book"}
              <ExternalLink size={10} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
